import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Navbar, Footer } from "@/components/site-chrome";
import { LiveBusMap } from "@/components/live-bus-map";
import { HeroVisual } from "@/components/landing/hero-visual";
import { ProductEcosystem } from "@/components/landing/product-ecosystem";
import { ParentExperience } from "@/components/landing/parent-experience";
import {
  AISection, AnalyticsSection,
  ImplementationTimeline,
} from "@/components/landing/feature-sections";

// ─── Poster palette: pure white, jet black, gold ────────────────────────────
const CREAM   = "#FFFFFF";
const CREAM2  = "#F9F8F6";
const WINE    = "#C9A84C";
const WINE_L  = "#E2C97E";
const CHARCOAL = "#0D0D0D";
const SAND    = "#C9A84C";
const FONT_DISPLAY = "'Georgia', 'Times New Roman', serif";
const FONT_SANS = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif";

const EASE = [0.16, 1, 0.3, 1] as const;

export function LandingPage() {
  return (
    <div className="min-h-screen" style={{ background: CREAM, fontFamily: FONT_SANS }}>
      <Navbar />
      <HeroVisual />
      <section id="ecosystem"><ProductEcosystemSection /></section>
      <section id="experience"><ParentSection /></section>
      <section id="intelligence"><AISectionWrapper /></section>
      <section id="analytics"><AnalyticsSectionWrapper /></section>
      <section id="transport"><FleetSection /></section>
      <section id="implementation"><ImplementationSection /></section>
      <FinalCTASection />
      <Footer />
    </div>
  );
}

// ============================================================
// PRODUCT ECOSYSTEM
// ============================================================
function ProductEcosystemSection() {
  return (
    <section className="relative py-28 sm:py-36" style={{ background: CREAM2 }}>
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        <SectionLabel label="Product Ecosystem" />
        <Reveal delay={0.1}>
          <h2
            className="mt-8 max-w-3xl"
            style={{
              fontFamily: FONT_DISPLAY,
              color: CHARCOAL,
              lineHeight: 0.92,
              letterSpacing: "-0.03em",
              fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
              fontWeight: 700,
            }}
          >
            Eight products.
          </h2>
          <p
            className="mt-1 max-w-3xl"
            style={{
              fontFamily: FONT_DISPLAY,
              color: WINE,
              lineHeight: 0.92,
              letterSpacing: "-0.03em",
              fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
              fontWeight: 700,
              fontStyle: "italic",
            }}
          >
            One platform.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p
            className="mt-6 max-w-2xl text-base leading-relaxed sm:text-lg"
            style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: `${CHARCOAL}80` }}
          >
            Not another app. Not another payment gateway. Not another SMS tool.
            Each TriSphere product is a complete system — and they all share one ledger,
            one identity layer, and one intelligence engine.
          </p>
        </Reveal>
        <div className="mt-16">
          <ProductEcosystem />
        </div>
      </div>
    </section>
  );
}

// ============================================================
// PARENT EXPERIENCE
// ============================================================
function ParentSection() {
  return (
    <section className="relative py-28 sm:py-36" style={{ background: CREAM }}>
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        <ParentExperience />
      </div>
    </section>
  );
}

// ============================================================
// AI
// ============================================================
function AISectionWrapper() {
  return (
    <section className="relative overflow-hidden py-28 sm:py-36" style={{ background: CHARCOAL }}>
      {/* Warm glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 60% 40% at 30% 20%, ${WINE}14 0%, transparent 50%),
            radial-gradient(ellipse 50% 40% at 70% 80%, ${SAND}0C 0%, transparent 50%)
          `,
        }}
      />
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        <AISection />
      </div>
    </section>
  );
}

// ============================================================
// ANALYTICS
// ============================================================
function AnalyticsSectionWrapper() {
  return (
    <section className="relative py-28 sm:py-36" style={{ background: CREAM2 }}>
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        <AnalyticsSection />
      </div>
    </section>
  );
}

// ============================================================
// FLEET / TRANSPORT
// ============================================================
function FleetSection() {
  return (
    <section className="relative py-28 sm:py-36" style={{ background: CREAM }}>
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        <SectionLabel label="Transport" />
        <Reveal delay={0.1}>
          <h2
            className="mt-8 max-w-3xl"
            style={{
              fontFamily: FONT_DISPLAY,
              color: CHARCOAL,
              lineHeight: 0.92,
              letterSpacing: "-0.03em",
              fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
              fontWeight: 700,
            }}
          >
            Every bus. Every route.
          </h2>
          <p
            className="mt-1 max-w-3xl"
            style={{
              fontFamily: FONT_DISPLAY,
              color: WINE,
              lineHeight: 0.92,
              letterSpacing: "-0.03em",
              fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
              fontWeight: 700,
              fontStyle: "italic",
            }}
          >
            Every child. Tracked.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p
            className="mt-6 max-w-2xl text-base leading-relaxed sm:text-lg"
            style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: `${CHARCOAL}80` }}
          >
            GPS-tracked bus routes with road-following navigation, live traffic, student
            boarding verification, driver behavior scoring, and AI route optimization.
            Parents see ETAs. Schools see compliance. Children see safety.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div
            className="mt-14 overflow-hidden"
            style={{ border: `1.5px solid ${CHARCOAL}20`, boxShadow: "0 24px 64px -28px rgba(201,168,76,0.15)" }}
          >
            <LiveBusMap wialon autoTick height={500} title="Fleet Command Center" canViewTrails canViewHistory />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============================================================
// IMPLEMENTATION
// ============================================================
function ImplementationSection() {
  return (
    <section className="relative py-28 sm:py-36" style={{ background: CREAM2 }}>
      <div className="relative mx-auto max-w-6xl px-6 sm:px-8">
        <SectionLabel label="Implementation" />
        <Reveal delay={0.1}>
          <h2
            className="mt-8 max-w-3xl"
            style={{
              fontFamily: FONT_DISPLAY,
              color: CHARCOAL,
              lineHeight: 0.92,
              letterSpacing: "-0.03em",
              fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
              fontWeight: 700,
            }}
          >
            From paperwork to platform
          </h2>
          <p
            className="mt-1 max-w-3xl"
            style={{
              fontFamily: FONT_DISPLAY,
              color: WINE,
              lineHeight: 0.92,
              letterSpacing: "-0.03em",
              fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
              fontWeight: 700,
              fontStyle: "italic",
            }}
          >
            in five weeks.
          </p>
        </Reveal>
        <div className="mt-20">
          <ImplementationTimeline />
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FINAL CTA — wine-red poster with ruled border
// ============================================================
function FinalCTASection() {
  return (
    <section className="relative overflow-hidden py-32 sm:py-40" style={{ background: CHARCOAL }}>
      {/* Gold grain texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
        }}
      />

      {/* Top + bottom ruled bands */}
      <div className="absolute top-0 left-0 right-0 h-2" style={{ background: SAND }} />
      <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: SAND }} />

      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <div
          className="relative p-12 sm:p-16"
          style={{ border: `1.5px solid ${SAND}40` }}
        >
          {/* Sand corners */}
          <SandCorner position="tl" />
          <SandCorner position="tr" />
          <SandCorner position="bl" />
          <SandCorner position="br" />

          <Reveal>
            <div className="mb-8 flex items-center justify-center gap-4">
              <div className="h-px w-12" style={{ background: SAND }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: SAND, fontFamily: FONT_SANS }}>
                Get Started
              </span>
              <div className="h-px w-12" style={{ background: SAND }} />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              className="text-white"
              style={{
                fontFamily: FONT_DISPLAY,
                lineHeight: 0.92,
                letterSpacing: "-0.03em",
                fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
                fontWeight: 700,
              }}
            >
              Build the future
            </h2>
            <p
              className="mt-1"
              style={{
                fontFamily: FONT_DISPLAY,
                color: SAND,
                lineHeight: 0.92,
                letterSpacing: "-0.03em",
                fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
                fontWeight: 700,
                fontStyle: "italic",
              }}
            >
              of education.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p
              className="mx-auto mt-8 max-w-xl text-base leading-relaxed sm:text-lg"
              style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: "rgba(255,255,255,0.65)" }}
            >
              Every institution deserves infrastructure worthy of the students it serves.
              See TriSphere in action — a 30-minute private demonstration tailored to
              your institution.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                asChild
                size="lg"
                className="h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] transition-all hover:opacity-90"
                style={{ background: SAND, color: CHARCOAL, border: "none", fontFamily: FONT_SANS }}
              >
                <Link to="/auth">Schedule Private Demonstration</Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="lg"
                className="h-12 rounded-none px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-all hover:bg-white/10"
                style={{ border: `1.5px solid ${SAND}50`, fontFamily: FONT_SANS }}
              >
                <Link to="/auth">Start Free</Link>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <p
              className="mt-12 text-[11px] font-medium uppercase tracking-[0.2em]"
              style={{ color: "rgba(255,255,255,0.35)", fontFamily: FONT_SANS }}
            >
              Built in Kampala · Designed for Africa · Ready for the World
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Sand corner decoration
// ============================================================
function SandCorner({ position }: { position: "tl" | "tr" | "bl" | "br" }) {
  const pos = {
    tl: { top: -1, left: -1, rotate: "0deg" as const },
    tr: { top: -1, right: -1, rotate: "90deg" as const },
    bl: { bottom: -1, left: -1, rotate: "-90deg" as const },
    br: { bottom: -1, right: -1, rotate: "180deg" as const },
  }[position];
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" className="absolute" style={pos}>
      <path d="M 2 2 L 2 24 M 2 2 L 24 2" stroke={SAND} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <circle cx="2" cy="2" r="2.5" fill={SAND} />
    </svg>
  );
}

// ============================================================
// Shared primitives
// ============================================================
function SectionLabel({ label, dark }: { label: string; dark?: boolean }) {
  return (
    <Reveal>
      <div className="flex items-center gap-4">
        <div className="h-px w-10" style={{ background: dark ? "rgba(255,255,255,0.25)" : SAND }} />
        <span
          className="text-[10px] font-bold uppercase tracking-[0.3em]"
          style={{ color: dark ? "rgba(255,255,255,0.55)" : SAND, fontFamily: FONT_SANS }}
        >
          {label}
        </span>
      </div>
    </Reveal>
  );
}

function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay, duration: 0.7, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export default LandingPage;
